// MemberRecus.jsx — Espace membre : mes reçus fiscaux (dons) téléchargeables.
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { downloadAuthed } from '@/lib/media';
import { toast } from 'sonner';
import { Loader2, Receipt, Download, HeartHandshake } from 'lucide-react';

const fmtDate = (s) => { try { return new Date(s).toLocaleDateString('fr-FR'); } catch { return s; } };

export default function MemberRecus() {
  const [dons, setDons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get('/dons/mes-recus'); setDons(data.dons || []); }
      catch { toast.error('Erreur de chargement'); }
      finally { setLoading(false); }
    })();
  }, []);

  const download = (d) =>
    downloadAuthed(`/api/dons/${d.id}/recu`, `recu-fiscal-${d.numero_recu}.pdf`).catch(() => toast.error('Téléchargement impossible'));

  const total = dons.reduce((s, d) => s + (d.montant || 0), 0);

  return (
    <div className="max-w-3xl mx-auto" data-testid="member-recus-page">
      <header className="mb-6">
        <h1 className="font-display text-2xl sm:text-3xl font-black text-slate-900 flex items-center gap-2">
          <Receipt className="w-6 h-6" /> Mes reçus fiscaux
        </h1>
        <p className="text-sm text-slate-500 mt-1">Vos reçus de dons, à joindre à votre déclaration d&apos;impôts.</p>
      </header>

      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : dons.length === 0 ? (
        <div className="card text-center py-14" data-testid="member-recus-empty">
          <HeartHandshake className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <h2 className="font-semibold text-slate-900">Aucun reçu pour l&apos;instant</h2>
          <p className="text-sm text-slate-500 mt-1">Vos reçus fiscaux apparaîtront ici dès qu&apos;un don sera enregistré par le bureau.</p>
        </div>
      ) : (
        <>
          <div className="card mb-4 flex items-center justify-between" data-testid="member-recus-total">
            <span className="text-sm text-slate-500">Total de vos dons</span>
            <span className="font-display text-xl font-black" style={{ color: 'var(--theme-primary)' }}>{total.toFixed(2)} €</span>
          </div>
          <div className="card divide-y divide-slate-100">
            {dons.map((d) => (
              <div key={d.id} className="py-3 flex items-center justify-between gap-3" data-testid={`member-recu-${d.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-slate-900">{(d.montant || 0).toFixed(2)} €<span className="ml-2 text-xs font-mono text-slate-400">n° {d.numero_recu}</span></div>
                  <div className="text-xs text-slate-500">{fmtDate(d.date_don)}</div>
                </div>
                <button onClick={() => download(d)} className="btn-secondary flex items-center gap-1.5 !py-1.5 !px-3" data-testid={`member-recu-download-${d.id}`}><Download className="w-4 h-4" /> Télécharger</button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
